// Workbook ⇄ server for v2 projects:
//   buildWorkbook — structure + content (Excel wins for sheets already in the workbook, the
//                   server fills the rest) → write every sheet → save the content back.
//   saveWorkbook  — read the v2 sheets and save their content (structure unchanged).

import { fetchProjectStructure, fetchStructureSheets, saveStructureSheets } from "../../api/projectStructureApi.js";
import { planWorkbook, savableSheets } from "./workbookPlan.js";
import { inspectWorkbook, readV2Contents, writeWorkbook } from "./workbookSync.js";

export class WorkbookSyncError extends Error {}

const projectInfo = (project, settings) => ({
  project: project.name,
  date: project.date,
  client: project.customer?.name || "",
  docNo: project.doc_number || "",
  docTitle: settings.document?.title || "",
  preparedBy: settings.document?.preparedBy || "",
  location: project.location || "",
});

const toPayload = (plan, contents) =>
  savableSheets(plan).map((s) => ({ object_id: s.sourceId, node_id: s.nodeId, rows: contents[`${s.sourceId}|${s.nodeId}`] || [] }));

/**
 * confirmRemove(list of { name, rows }) → Promise<boolean>: asked before v2 sheets that are no
 * longer in the structure are deleted together with their works.
 */
export async function buildWorkbook({ token, project, confirmRemove, onProgress }) {
  const structure = await fetchProjectStructure(token, project.id);
  if (!structure.exists) throw new WorkbookSyncError("პროექტს სტრუქტურა ჯერ არ აქვს — გახსენით „ნაკვეთი და სტრუქტურა“.");
  const settings = structure.settings || {};
  const [server, inExcel] = await Promise.all([fetchStructureSheets(token, project.id), readV2Contents()]);
  const contents = {};
  server.sheets.forEach((s) => (contents[`${s.object_id}|${s.node_id}`] = s.rows));
  Object.assign(contents, inExcel);

  const plan = planWorkbook({ site: structure.site, trees: structure.trees, settings, info: projectInfo(project, settings), contents });
  const check = await inspectWorkbook(plan);
  if (check.conflicts.length) {
    throw new WorkbookSyncError(`ამ სახელით უკვე არის სხვა ფურცელი: ${check.conflicts.join(", ")}. გადაარქვით ან წაშალეთ და სცადეთ თავიდან.`);
  }
  const removing = check.toRemove.map((s) => ({ ...s, rows: (inExcel[s.identity] || []).length }));
  if (removing.some((s) => s.rows > 0) && !(await confirmRemove(removing))) return { cancelled: true };

  const result = await writeWorkbook(plan, { removeIds: removing.map((s) => s.identity), onProgress });
  const saved = await saveStructureSheets(token, project.id, { revision: structure.revision, sheets: toPayload(plan, contents) });
  return { ...result, savedRows: saved.rows };
}

export async function saveWorkbook({ token, project }) {
  const structure = await fetchProjectStructure(token, project.id);
  if (!structure.exists) throw new WorkbookSyncError("პროექტს სტრუქტურა ჯერ არ აქვს.");
  const inExcel = await readV2Contents();
  if (!Object.keys(inExcel).length) throw new WorkbookSyncError("ამ წიგნში პროექტის ფურცლები ვერ მოიძებნა — ჯერ ააწყვეთ Excel.");
  const settings = structure.settings || {};
  const plan = planWorkbook({ site: structure.site, trees: structure.trees, settings, info: projectInfo(project, settings), contents: inExcel });
  const present = toPayload(plan, inExcel).filter((s) => inExcel[`${s.object_id}|${s.node_id}`]);
  try {
    return await saveStructureSheets(token, project.id, { revision: structure.revision, sheets: present });
  } catch (err) {
    if (err.status === 409) throw new WorkbookSyncError("სტრუქტურა შეიცვალა — ჯერ განაახლეთ Excel.");
    throw err;
  }
}
